import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

interface ImportGuestsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

interface GuestRow {
    fullname: string;
    invitecode?: string;
    events?: string; // "IE", "ES", "IE,ES", "PB"
    plusoneallowed?: string; // "true", "yes", "1"
    pluschildren?: string; // "true", "yes", "1"
    country?: string; // "IE", "ES", "UK"
}

export const ImportGuestsModal: React.FC<ImportGuestsModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [importMode, setImportMode] = useState<'file' | 'paste'>('file');
    const [pasteContent, setPasteContent] = useState('');
    const [preview, setPreview] = useState<GuestRow[]>([]);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
    const [error, setError] = useState('');
    const [logs, setLogs] = useState<string[]>([]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setError('');
            parseData(selectedFile);
        }
    };

    const handlePasteSubmit = () => {
        if (!pasteContent.trim()) {
            setError('Please paste some data first.');
            return;
        }
        setError('');
        
        let dataToParse = pasteContent;
        // If data doesn't seem to have tabs or commas, but has multiple spaces, it might be space-aligned text
        if (!dataToParse.includes('\t') && !dataToParse.includes(',')) {
            // Replace 2 or more spaces with a tab to help PapaParse detect columns
            dataToParse = dataToParse.replace(/ {2,}/g, '\t');
        }

        parseData(dataToParse);
    };

    const processParseResults = (results: Papa.ParseResult<GuestRow>) => {
        if (results.errors.length > 0) {
            // Check if it's just a trailing empty line error
            const actualErrors = results.errors.filter(e => e.code !== 'TooFewFields' || e.row !== undefined);
            if (actualErrors.length > 0) {
                setError(`Error parsing data: ${actualErrors[0].message}`);
                return;
            }
        }
        // Basic validation of validity
        const validRows = results.data.filter(r => r.fullname && r.fullname.trim().length > 0);
        if (validRows.length === 0) {
            setError('No valid rows found. Please ensure "fullName" column exists.');
            return;
        }

        // Pre-process rows to fix empty strings in optional fields
        // Note: transformHeader makes all keys lowercase and maps aliases
        const processedRows = validRows.map(row => ({
            ...row,
            fullname: row.fullname.trim(),
            events: row.events?.trim() || undefined,
            invitecode: row.invitecode?.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() || undefined,
            plusoneallowed: row.plusoneallowed?.trim() || undefined,
            pluschildren: row.pluschildren?.trim() || undefined,
            country: row.country?.trim().toUpperCase() || undefined
        }));

        setPreview(processedRows);
        setLogs([`Found ${processedRows.length} guests to import.`]);
    };

    const parseData = (data: File | string) => {
        Papa.parse<GuestRow>(data as any, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => {
                const h = header.trim().toLowerCase();
                if (['name', 'guest', 'guest name', 'full name'].includes(h)) return 'fullname';
                if (['code', 'invite', 'invite code', 'clave'].includes(h)) return 'invitecode';
                if (['event', 'rsvp', 'events'].includes(h)) return 'events';
                if (['+1', 'plus one', 'plusone', 'bring guest', 'acompañante'].includes(h)) return 'plusoneallowed';
                if (['kids', '+kids', 'children', 'plus children', 'niños'].includes(h)) return 'pluschildren';
                if (['pais', 'país', 'location', 'country'].includes(h)) return 'country';
                return h;
            },
            complete: processParseResults,
            error: (err) => {
                setError(`Failed to read data: ${err.message}`);
            }
        });
    };

    const processBatch = async () => {
        if (!preview.length) return;

        setUploading(true);
        setProgress({ current: 0, total: preview.length });
        setLogs(prev => [...prev, 'Starting import...']);

        const upsertGuest = httpsCallable(functions, 'upsertGuest');
        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < preview.length; i++) {
            const row = preview[i];
            try {
                // Transform data
                const guestCountry = (row.country || 'IE').toUpperCase();
                
                // Spanish guests default to ES and PB, others default to all
                let defaultEvents = ['IE', 'ES', 'PB'];
                if (guestCountry === 'ES') defaultEvents = ['ES', 'PB'];
                else if (guestCountry === 'UK') defaultEvents = ['IE', 'ES', 'PB'];

                const eventsString = (row.events || '').trim();
                const events = eventsString
                    ? eventsString.split(/[|/;, ]+/).map(c => c.trim().toUpperCase()).filter(Boolean)
                    : defaultEvents;

                // Double check if split resulted in empty array (e.g. string was just spaces or separators)
                const finalEvents = (events.length > 0) ? events : defaultEvents;

                const trueValues = ['true', 'yes', '1', 'y', 'si', 'sí', 's'];
                const plusOne = trueValues.includes((row.plusoneallowed || '').toLowerCase());
                const plusKids = trueValues.includes((row.pluschildren || '').toLowerCase());

                await upsertGuest({
                    fullName: row.fullname,
                    inviteCode: row.invitecode || undefined,
                    events: finalEvents,
                    plusOneAllowed: plusOne,
                    plusChildren: plusKids,
                    country: guestCountry
                });
                successCount++;
            } catch (err: any) {
                failCount++;
                setLogs(prev => [...prev, `Failed to import ${row.fullname}: ${err.message}`]);
            }
            setProgress({ current: i + 1, total: preview.length });
        }

        setLogs(prev => [...prev, `Import complete. Success: ${successCount}, Failed: ${failCount}`]);
        setUploading(false);

        if (successCount > 0) {
            setTimeout(() => {
                onSuccess();
                onClose();
                // Reset
                setPasteContent('');
                setPreview([]);
                setLogs([]);
                setProgress(null);
            }, 2000);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Import Guests from CSV">
            <div className="flex flex-col gap-6 font-sans text-secondary">
                {!uploading && !progress ? (
                    <>
                        <div className="bg-bg-subtle p-5 rounded-xl border border-gray-200">
                            <p className="font-bold text-base uppercase tracking-widest text-secondary mb-2">CSV Format Instructions:</p>
                            <p className="text-sm text-secondary-light leading-relaxed">
                                Ensure your CSV has the following headers (case-sensitive):
                            </p>
                            <ul className="mt-3 space-y-1.5 list-disc list-inside text-sm text-secondary-light">
                                <li><code className="font-mono bg-gray-100 px-1 rounded text-secondary">fullName</code> (Required)</li>
                                <li><code className="font-mono bg-gray-100 px-1 rounded text-secondary">inviteCode</code> (Optional)</li>
                                <li><code className="font-mono bg-gray-100 px-1 rounded text-secondary">events</code> (Optional, e.g. "IE | ES | PB")</li>
                                <li><code className="font-mono bg-gray-100 px-1 rounded text-secondary">plusOneAllowed</code> (Optional, "yes"/"no")</li>
                                <li><code className="font-mono bg-gray-100 px-1 rounded text-secondary">plusChildren</code> (Optional, "yes"/"no")</li>
                                <li><code className="font-mono bg-gray-100 px-1 rounded text-secondary">country</code> (Optional, "IE", "ES", or "UK")</li>
                            </ul>
                        </div>

                        <div className="flex bg-gray-100 p-1 rounded-xl mb-4">
                            <button
                                className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${importMode === 'file' ? 'bg-white shadow-sm text-secondary' : 'text-secondary-light hover:text-secondary'}`}
                                onClick={() => { setImportMode('file'); setPreview([]); setError(''); setPasteContent(''); }}
                            >
                                Upload CSV File
                            </button>
                            <button
                                className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${importMode === 'paste' ? 'bg-white shadow-sm text-secondary' : 'text-secondary-light hover:text-secondary'}`}
                                onClick={() => { setImportMode('paste'); setPreview([]); setError(''); }}
                            >
                                Paste from Sheets
                            </button>
                        </div>

                        {importMode === 'file' ? (
                            <div>
                                <input
                                    type="file"
                                    accept=".csv"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    className="block w-full text-base text-secondary-light file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-primary-light/20 file:text-primary-dark hover:file:bg-primary-light/30 transition-all cursor-pointer"
                                />
                                {error && (
                                    <p className="text-red-600 text-sm mt-2 font-medium">{error}</p>
                                )}
                            </div>
                        ) : (
                            <div>
                                <textarea
                                    className="w-full h-32 p-3 text-sm bg-white border border-gray-200 rounded-xl outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary transition-all resize-none font-mono"
                                    placeholder="Paste cells directly from your Google Sheet here..."
                                    value={pasteContent}
                                    onChange={(e) => setPasteContent(e.target.value)}
                                />
                                <div className="flex justify-between items-center mt-2">
                                    {error ? (
                                        <p className="text-red-600 text-sm font-medium">{error}</p>
                                    ) : (
                                        <p className="text-secondary-light text-sm">Rows will be auto-detected.</p>
                                    )}
                                    <Button variant="outline" size="sm" onClick={handlePasteSubmit}>Preview Data</Button>
                                </div>
                            </div>
                        )}

                        {preview.length > 0 && (
                            <div className="space-y-3">
                                <p className="font-bold text-base uppercase tracking-widest text-secondary">Preview <span className="text-secondary-light font-normal">({preview.length} guests)</span></p>
                                <div className="max-h-[300px] overflow-y-auto border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                    <table className="w-full text-sm text-left border-collapse">
                                        <thead className="bg-bg-subtle sticky top-0 border-b border-gray-200">
                                            <tr>
                                                <th className="px-4 py-3 font-bold text-secondary-light uppercase tracking-widest">Name</th>
                                                <th className="px-4 py-3 font-bold text-secondary-light uppercase tracking-widest">Code</th>
                                                <th className="px-4 py-3 font-bold text-secondary-light uppercase tracking-widest">Events</th>
                                                <th className="px-4 py-3 font-bold text-secondary-light uppercase tracking-widest">+1</th>
                                                <th className="px-4 py-3 font-bold text-secondary-light uppercase tracking-widest">+Kids</th>
                                                <th className="px-4 py-3 font-bold text-secondary-light uppercase tracking-widest">Country</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                            {preview.slice(0, 10).map((row, i) => (
                                                <tr key={i} className="hover:bg-bg-subtle transition-colors">
                                                    <td className="px-4 py-3 text-secondary font-medium">{row.fullname}</td>
                                                    <td className="px-4 py-3 font-mono text-secondary-light">{row.invitecode || '-'}</td>
                                                    <td className="px-4 py-3 text-secondary-light">{row.events || 'Auto'}</td>
                                                    <td className="px-4 py-3 text-secondary-light">{row.plusoneallowed || '-'}</td>
                                                    <td className="px-4 py-3 text-secondary-light">{row.pluschildren || '-'}</td>
                                                    <td className="px-4 py-3 text-secondary-light font-bold text-primary">{row.country || 'IE'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {preview.length > 10 && (
                                        <div className="px-4 py-3 bg-white text-center text-secondary-light text-[10px] border-t border-gray-100">
                                            And {preview.length - 10} more records...
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 mt-4">
                            <Button variant="outline" className="rounded-full px-6" onClick={onClose}>Cancel</Button>
                            <Button className="rounded-full px-6 bg-primary text-white" onClick={processBatch} disabled={preview.length === 0 || !!error}>
                                Import {preview.length} Guests
                            </Button>
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col gap-4 items-center py-6 text-center">
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-50">
                            <div className="h-full bg-primary transition-all duration-300 ease-out"
                                style={{
                                    width: `${progress ? (progress.current / progress.total) * 100 : 0}%`,
                                }} />
                        </div>
                        <div className="space-y-1">
                            <p className="font-bold text-secondary uppercase tracking-widest text-base">
                                Importing...
                            </p>
                            <p className="text-secondary-light text-sm font-mono">
                                {progress?.current} of {progress?.total} processed
                            </p>
                        </div>
                        <div className="w-full max-h-[200px] overflow-y-auto bg-bg-subtle p-4 rounded-xl border border-gray-200 text-[10px] font-mono text-secondary-light space-y-1 text-left">
                            {logs.map((log, i) => (
                                <div key={i} className="border-b border-gray-100 pb-1 flex gap-2">
                                    <span className="opacity-40">[{i + 1}]</span>
                                    <span>{log}</span>
                                </div>
                            ))}
                        </div>
                        {progress?.current === progress?.total && (
                            <Button onClick={onClose} variant="outline" size="sm" className="rounded-full px-8 mt-2">Close</Button>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};
